import {
  type ButtonHTMLAttributes,
  type FC,
  type ReactNode,
  type Ref,
} from "react";

import { clsx } from "clsx";

import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { type CaseStatusTone, CaseStepDot } from "@styled/case_steps";

// A guided empty-state bubble: an icon chip, a label, and a short body, sitting
// in a tablist relationship with the panel below it. Bubbles SCOPE, they do not
// act.
//
// The brand ramp ships on `indigo-*`: the app's @theme block overrides Tailwind
// indigo with the Workflow teal ramp (indigo-300 renders #a9d4d2, indigo-500
// #179694). `teal-*` is stock Tailwind and is NOT the brand colour.

type GuidedBubbleDensity = "default" | "sm";

// The chip (26px) plus its 9px gap — body copy hangs under the label, not the icon.
const BODY_INDENT = "pl-[35px]";

const DENSITY_STYLE: Record<GuidedBubbleDensity, string> = {
  default: "gap-[7px] pt-[14px] pr-4 pb-4 pl-4",
  sm: "gap-[2px] px-[14px] py-[9px]",
};

// Selected is lit (where you are) with a tail into the panel; the others sit
// quiet in the page.
const SELECTED_STYLE = "surface-lit";

export const GuidedBubble: FC<
  {
    // The icon in its chip, or for a case's step its state's dot.
    mark: { icon: IconProp } | { tone: CaseStatusTone };
    label: string;
    body: ReactNode;
    sub?: ReactNode;
    tag?: string;
    selected: boolean;
    density?: GuidedBubbleDensity;
    // The cases-list bubble is permanently selected with nothing to toggle, so
    // it renders as plain content: no button, no tab role, not focusable.
    interactive?: boolean;
    ref?: Ref<HTMLButtonElement>;
  } & Pick<
    ButtonHTMLAttributes<HTMLButtonElement>,
    "id" | "onClick" | "onKeyDown" | "tabIndex" | "aria-controls"
  >
> = ({
  mark,
  label,
  body,
  sub,
  tag,
  selected,
  density = "default",
  interactive = true,
  ref,
  ...button
}) => {
  const className = clsx(
    "relative flex flex-col rounded-2xl text-left transition-[box-shadow,background-color] duration-150 motion-reduce:transition-none",
    interactive &&
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500",
    DENSITY_STYLE[density],
    selected
      ? SELECTED_STYLE
      : clsx("surface-quiet", interactive && "hover:bg-quiet-hover"),
  );

  const content = (
    <>
      <span className="flex items-center gap-[9px]">
        {"tone" in mark ? (
          <CaseStepDot tone={mark.tone} size="chip" />
        ) : (
          <span
            className={clsx(
              "flex size-[26px] shrink-0 items-center justify-center rounded-lg",
              selected
                ? "bg-white text-indigo-700 ring-1 ring-indigo-500/20"
                : "text-muted ring-hairline bg-white ring-1",
            )}
          >
            <FontAwesomeIcon icon={mark.icon} aria-hidden />
          </span>
        )}

        <span
          className={clsx(
            "text-[13.5px] font-semibold",
            selected ? "text-ink" : "text-body",
          )}
        >
          {label}
        </span>

        {tag && (
          <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold tracking-[0.08em] text-indigo-700 uppercase ring-1 ring-indigo-500/25">
            {tag}
          </span>
        )}
      </span>

      <span className={clsx(BODY_INDENT, "text-body text-[12px]")}>{body}</span>

      {sub && (
        <span
          className={clsx(
            BODY_INDENT,
            "text-muted",
            density === "sm" ? "text-[11px]" : "text-[10.5px]",
          )}
        >
          {sub}
        </span>
      )}

      {/* The tail: a 14px square rotated 45°, half-clipped by the bubble's own
          edge, so it reads as a triangle pointing into the panel below. Hidden
          once the grid stacks — below md the panel is no longer adjacent. */}
      {selected && (
        <span
          aria-hidden
          className="bg-lit-floor absolute bottom-[-7px] left-1/2 hidden size-[14px] -translate-x-1/2 rotate-45 border-r border-b border-indigo-500/35 md:block"
        />
      )}
    </>
  );

  if (!interactive) return <div className={className}>{content}</div>;

  // Pointer on the button only — the inert list bubble renders the same
  // `className`, and a pointer there would promise a click that never comes.
  return (
    <button
      {...button}
      ref={ref}
      type="button"
      role="tab"
      aria-selected={selected}
      className={clsx(className, "cursor-pointer")}
    >
      {content}
    </button>
  );
};
